import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { VUKZ } from '../theme/vukzTheme';
import { useSportsLang } from '../contexts/SportsLangContext';
import { getDashboardStats, cancelSession, reprocessSession } from '../services/biomechanicsService';
import {
  Activity, BarChart3, CheckCircle2, Clock, AlertTriangle, XCircle,
  Video, Waves, ArrowRight, TrendingUp, Cpu, Link2, Zap,
  Timer, Eye, RefreshCw, ChevronRight, Loader2, Layers, Ban, Tag,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════════
   VUKZ Design Tokens (scoped)
   ═══════════════════════════════════════════════════════════════════════════════ */
const T = {
  bg:         '#fafafa',
  card:       '#ffffff',
  text:       '#111111',
  textSoft:   '#6b7280',
  textMute:   '#9ca3af',
  border:     '#e5e7eb',
  accent:     '#111111',
  accentSoft: '#f3f4f6',
  success:    '#16a34a',
  successBg:  '#f0fdf4',
  warning:    '#f59e0b',
  warningBg:  '#fffbeb',
  error:      '#dc2626',
  errorBg:    '#fef2f2',
  info:       '#2563eb',
  infoBg:     '#eff6ff',
  cyan:       '#0891b2',
  cyanBg:     '#ecfeff',
};

const LABELS_I18N = {
  pt: {
    styleLabels: { freestyle: 'Livre', backstroke: 'Costas', butterfly: 'Borboleta', breaststroke: 'Peito', medley: 'Medley' },
    statusMeta: { completed: 'Concluídas', processing: 'Processando', pending: 'Na fila', failed: 'Falhas', cancelled: 'Canceladas' },
    subtitle: 'Dashboard de Análise Biomânica',
    loading: 'Carregando dashboard...', retry: 'Tentar novamente',
    newAnalysis: 'Nova Análise', refresh: 'Atualizar',
    sessions: 'sessões', successRate: (r) => `${r}% taxa de sucesso`,
    lanesLabel: (n) => `${n} raias mapeadas`, avgTime: 'Tempo médio',
    styleDistrib: 'Distribuição por Estilo',
    workerPerf: 'Performance do Worker',
    download: 'Download', inference: 'Inferência', total: 'Total',
    sample: 'Amostra', sessionsSuffix: 'sessões',
    raceLinks: 'Vinculação de Provas',
    linkedSessions: 'Sessões vinculadas', mappedLanes: 'Raias mapeadas',
    competitions: 'Competições', events: 'Provas (eventos)', withOfficial: 'Com resultado oficial',
    noLinked: 'Nenhuma sessão vinculada a provas oficiais ainda.\nUse o Swim Vision para vincular vídeos a competições.',
    dailyTrend: 'Sessões por Dia (últimos 14 dias)',
    completedLegend: 'Concluídas', totalLegend: 'Total',
    sessionTooltip: (n, c) => `${n} sessões (${c} ✓)`,
    recentSessions: 'Sessões Recentes', viewAll: 'Ver todas',
    noSessions: 'Nenhuma sessão encontrada', startFirst: 'Iniciar primeira análise',
    strokesAbbr: 'braç.',
    linkedToRaceTitle: 'Vinculada a resultado oficial',
    reprocessTitle: 'Reprocessar sessão', cancelTitle: 'Cancelar sessão',
    viewResultTitle: 'Resultado da Prova', annotateTitle: 'Anotar vídeo',
    confirmCancel: (id, st) => `Cancelar sessão #${id} (${st})?`,
    confirmReprocess: (id, st) => `Reprocessar sessão #${id} (${st})? Ela voltará para a fila.`,
    errCancel: 'Erro ao cancelar', errReprocess: 'Erro ao reprocessar',
    noData: 'Sem dados',
  },
  en: {
    styleLabels: { freestyle: 'Freestyle', backstroke: 'Backstroke', butterfly: 'Butterfly', breaststroke: 'Breaststroke', medley: 'Medley' },
    statusMeta: { completed: 'Completed', processing: 'Processing', pending: 'In queue', failed: 'Failed', cancelled: 'Cancelled' },
    subtitle: 'Biomechanical Analysis Dashboard',
    loading: 'Loading dashboard...', retry: 'Try again',
    newAnalysis: 'New Analysis', refresh: 'Refresh',
    sessions: 'sessions', successRate: (r) => `${r}% success rate`,
    lanesLabel: (n) => `${n} lanes mapped`, avgTime: 'Avg. time',
    styleDistrib: 'Style Distribution',
    workerPerf: 'Worker Performance',
    download: 'Download', inference: 'Inference', total: 'Total',
    sample: 'Sample', sessionsSuffix: 'sessions',
    raceLinks: 'Race Links',
    linkedSessions: 'Linked sessions', mappedLanes: 'Mapped lanes',
    competitions: 'Competitions', events: 'Events', withOfficial: 'With official result',
    noLinked: 'No sessions linked to official races yet.\nUse Swim Vision to link videos to competitions.',
    dailyTrend: 'Sessions per Day (last 14 days)',
    completedLegend: 'Completed', totalLegend: 'Total',
    sessionTooltip: (n, c) => `${n} sessions (${c} ✓)`,
    recentSessions: 'Recent Sessions', viewAll: 'View all',
    noSessions: 'No sessions found', startFirst: 'Start first analysis',
    strokesAbbr: 'str.',
    linkedToRaceTitle: 'Linked to official result',
    reprocessTitle: 'Reprocess session', cancelTitle: 'Cancel session',
    viewResultTitle: 'Race Result', annotateTitle: 'Annotate video',
    confirmCancel: (id, st) => `Cancel session #${id} (${st})?`,
    confirmReprocess: (id, st) => `Reprocess session #${id} (${st})? It will go back to queue.`,
    errCancel: 'Error cancelling', errReprocess: 'Error reprocessing',
    noData: 'No data',
  },
};

const STYLE_COLORS = {
  freestyle: '#2563eb', backstroke: '#7c3aed', butterfly: '#ea580c',
  breaststroke: '#16a34a', medley: '#0891b2',
};

/* ── Subcomponents ─────────────────────────────────────────────────────────── */

const StatCard = ({ icon: Icon, label, value, unit, color, bg, subtext }) => (
  <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-all duration-200"
    style={{ borderColor: T.border }}>
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: bg || T.accentSoft }}>
        <Icon size={16} style={{ color: color || T.accent }} />
      </div>
      <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>{label}</span>
    </div>
    <div className="flex items-baseline gap-1.5">
      <span className="text-2xl font-black" style={{ color: T.text }}>{value}</span>
      {unit && <span className="text-xs font-semibold" style={{ color: T.textSoft }}>{unit}</span>}
    </div>
    {subtext && <p className="text-[10px] mt-1" style={{ color: T.textMute }}>{subtext}</p>}
  </div>
);

const MiniBar = ({ items, total }) => {
  if (!total) return null;
  return (
    <div className="flex rounded-full overflow-hidden h-2.5 w-full" style={{ backgroundColor: T.accentSoft }}>
      {items.map((it, i) => (
        <div key={i} style={{ width: `${(it.value / total) * 100}%`, backgroundColor: it.color }}
          title={`${it.label}: ${it.value}`} />
      ))}
    </div>
  );
};

const SessionRow = ({ s, index, onCancel, onReprocess }) => {
  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;
  const STATUS_META = {
    completed:  { icon: CheckCircle2, color: T.success, bg: T.successBg },
    processing: { icon: Loader2, color: T.info, bg: T.infoBg },
    pending:    { icon: Clock, color: T.warning, bg: T.warningBg },
    failed:     { icon: XCircle, color: T.error, bg: T.errorBg },
    cancelled:  { icon: Ban, color: T.textMute, bg: T.accentSoft },
  };
  const STYLE_LABELS = L.styleLabels;
  const meta = STATUS_META[s.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const canCancel = s.status === 'pending' || s.status === 'processing';
  const canReprocess = s.status === 'failed' || s.status === 'cancelled';
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b"
      style={{ borderColor: T.border }}>
      <span className="text-[10px] font-bold w-5 text-center" style={{ color: T.textMute }}>{index + 1}</span>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: meta.bg }}>
        <StatusIcon size={14} style={{ color: meta.color }} className={s.status === 'processing' ? 'animate-spin' : ''} />
      </div>
      <Link to={s.status === 'completed' ? `/swim-vision/result/${s.session_id}` : `/swim-vision?session=${s.session_id}`}
        className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold truncate" style={{ color: T.text }}>
            #{s.session_id}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
            style={{ backgroundColor: STYLE_COLORS[s.swimming_style] + '18', color: STYLE_COLORS[s.swimming_style] }}>
            {STYLE_LABELS[s.swimming_style] || s.swimming_style}
          </span>
          {s.official_result_id && (
            <Link2 size={10} style={{ color: T.cyan }} title="Vinculada a resultado oficial" />
          )}
        </div>
        <span className="text-[10px]" style={{ color: T.textMute }}>
          {s.video_filename ? s.video_filename.substring(0, 40) : '—'}
        </span>
      </Link>
      <div className="text-right shrink-0">
        {s.stroke_count != null && (
          <div className="text-xs font-bold" style={{ color: T.text }}>{s.stroke_count} <span style={{ color: T.textMute }}>{L.strokesAbbr}</span></div>
        )}
        {s.processing_time_ms != null && (
          <div className="text-[10px]" style={{ color: T.textMute }}>{(s.processing_time_ms / 1000).toFixed(1)}s</div>
        )}
      </div>
      {canReprocess && (
        <button
          onClick={() => onReprocess && onReprocess(s.session_id, s.status)}
          className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
          title={L.reprocessTitle}>
          <RefreshCw size={14} style={{ color: T.accent }} />
        </button>
      )}
      {canCancel && (
        <button
          onClick={() => onCancel && onCancel(s.session_id, s.status)}
          className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
          title={L.cancelTitle}>
          <Ban size={14} style={{ color: T.error }} />
        </button>
      )}
      {s.status === 'completed' && s.stroke_count > 0 && (
        <Link to={`/swim-vision/result/${s.session_id}`}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          title={L.viewResultTitle}
          onClick={(e) => e.stopPropagation()}>
          <BarChart3 size={14} style={{ color: T.accent }} />
        </Link>
      )}
      {s.status === 'completed' && (
        <Link to={`/swim-vision/annotate/${s.session_id}`}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          title={L.annotateTitle}
          onClick={(e) => e.stopPropagation()}>
          <Tag size={14} style={{ color: T.cyan || '#0891b2' }} />
        </Link>
      )}
      <ChevronRight size={14} style={{ color: T.textMute }} />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════════════════ */
const SwimVisionDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;
  const STYLE_LABELS = L.styleLabels;

  const handleCancel = async (sessionId, currentStatus) => {
    if (!window.confirm(L.confirmCancel(sessionId, currentStatus))) return;
    setCancelling(sessionId);
    try {
      await cancelSession(sessionId, `Cancelled from dashboard (was ${currentStatus})`);
      await fetchDashboard();
    } catch (err) {
      console.error('[Dashboard] cancel error:', err);
      alert(`${L.errCancel}: ${err.response?.data?.error || err.message}`);
    } finally {
      setCancelling(null);
    }
  };

  const handleReprocess = async (sessionId, currentStatus) => {
    if (!window.confirm(L.confirmReprocess(sessionId, currentStatus))) return;
    try {
      await reprocessSession(sessionId);
      await fetchDashboard();
    } catch (err) {
      console.error('[Dashboard] reprocess error:', err);
      alert(`${L.errReprocess}: ${err.response?.data?.error || err.message}`);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getDashboardStats();
      setData(resp.data?.data || resp.data);
    } catch (err) {
      console.error('[Dashboard] fetch error:', err);
      setError(err.message || L.loading);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const totals = data?.totals || {};
  const styles = data?.styles || {};
  const perf = data?.performance || {};
  const links = data?.race_links || {};
  const official = data?.official_results || {};
  const recent = data?.recent_sessions || [];
  const daily = data?.daily_trend || [];

  // Style distribution bar data
  const styleBarItems = useMemo(() =>
    Object.entries(styles).map(([k, v]) => ({
      label: STYLE_LABELS[k] || k, value: v, color: STYLE_COLORS[k] || '#94a3b8',
    })).sort((a, b) => b.value - a.value),
    [styles]
  );

  // Daily trend max
  const maxDaily = useMemo(() => Math.max(...daily.map(d => d.sessions), 1), [daily]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="flex items-center gap-3">
          <Loader2 className="animate-spin" size={24} style={{ color: T.accent }} />
          <span className="text-sm font-semibold" style={{ color: T.textSoft }}>{L.loading}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <AlertTriangle size={32} style={{ color: T.warning }} className="mx-auto mb-3" />
          <p className="text-sm font-semibold" style={{ color: T.text }}>{error}</p>
          <button onClick={fetchDashboard}
            className="mt-3 px-4 py-2 rounded-lg text-xs font-bold text-white"
            style={{ backgroundColor: T.accent }}>
            {L.retry}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6 space-y-6">

        {/* ── Header ────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: T.accent }}>
              <Eye size={20} style={{ color: '#ffffff' }} />
            </div>
            <div>
              <h1 className="text-2xl font-black" style={{ color: T.text }}>Swim Vision</h1>
              <p className="text-xs" style={{ color: T.textSoft }}>
                {L.subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchDashboard}
              className="p-2 rounded-lg border hover:bg-gray-50 transition-colors"
              style={{ borderColor: T.border }} title={L.refresh}>
              <RefreshCw size={16} style={{ color: T.textSoft }} />
            </button>
            <button onClick={() => navigate('/swim-vision')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: T.accent }}>
              <Video size={14} />
              {L.newAnalysis}
            </button>
          </div>
        </div>

        {/* ── KPI Cards ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={Activity} label="Total" value={totals.sessions || 0} unit={L.sessions}
            color={T.accent} bg={T.accentSoft} />
          <StatCard icon={CheckCircle2} label={L.statusMeta.completed} value={totals.completed || 0}
            color={T.success} bg={T.successBg} subtext={L.successRate(totals.success_rate || 0)} />
          <StatCard icon={Clock} label={L.statusMeta.pending} value={totals.pending || 0}
            color={T.warning} bg={T.warningBg} />
          <StatCard icon={XCircle} label={L.statusMeta.failed} value={totals.failed || 0}
            color={T.error} bg={T.errorBg} />
          <StatCard icon={Link2} label={L.linkedSessions} value={links.linked_sessions || 0} unit={L.sessions}
            color={T.cyan} bg={T.cyanBg} subtext={L.lanesLabel(links.total_lane_links || 0)} />
          <StatCard icon={Zap} label={L.avgTime} value={perf.avg_processing_ms ? (perf.avg_processing_ms / 1000).toFixed(1) : '—'} unit="seg"
            color={T.info} bg={T.infoBg} subtext={`n=${perf.sample_size || 0}`} />
        </div>

        {/* ── Middle Row: Styles + Performance + Race Links ────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Style Distribution */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: T.border }}>
            <div className="flex items-center gap-2 mb-3">
              <Waves size={14} style={{ color: T.textSoft }} />
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
                {L.styleDistrib}
              </span>
            </div>
            <MiniBar items={styleBarItems} total={totals.sessions || 0} />
            <div className="mt-3 space-y-1.5">
              {styleBarItems.map(s => (
                <div key={s.label} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                    <span style={{ color: T.text }}>{s.label}</span>
                  </div>
                  <span className="font-bold" style={{ color: T.text }}>{s.value}</span>
                </div>
              ))}
              {styleBarItems.length === 0 && (
                <p className="text-[10px] text-center py-2" style={{ color: T.textMute }}>{L.noData}</p>
              )}
            </div>
          </div>

          {/* Processing Performance */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: T.border }}>
            <div className="flex items-center gap-2 mb-3">
              <Cpu size={14} style={{ color: T.textSoft }} />
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
                {L.workerPerf}
              </span>
            </div>
            <div className="space-y-3">
              {[
                { label: L.download, value: perf.avg_download_ms, color: '#6366f1' },
                { label: L.inference, value: perf.avg_inference_ms, color: '#f59e0b' },
                { label: L.total, value: perf.avg_processing_ms, color: T.accent },
              ].map(item => {
                const maxMs = perf.avg_processing_ms || 1;
                const pct = Math.min((item.value || 0) / maxMs * 100, 100);
                return (
                  <div key={item.label}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span style={{ color: T.textSoft }}>{item.label}</span>
                      <span className="font-bold" style={{ color: T.text }}>
                        {item.value ? `${(item.value / 1000).toFixed(1)}s` : '—'}
                      </span>
                    </div>
                    <div className="h-2 rounded-full" style={{ backgroundColor: T.accentSoft }}>
                      <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: item.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t" style={{ borderColor: T.border }}>
              <div className="flex items-center justify-between text-[10px]">
                <span style={{ color: T.textMute }}>{L.sample}:</span>
                <span className="font-bold" style={{ color: T.textSoft }}>{perf.sample_size || 0} {L.sessionsSuffix}</span>
              </div>
            </div>
          </div>

          {/* Race Links */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: T.border }}>
            <div className="flex items-center gap-2 mb-3">
              <Link2 size={14} style={{ color: T.textSoft }} />
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
                {L.raceLinks}
              </span>
            </div>
            <div className="space-y-3">
              {[
                { label: L.linkedSessions, value: links.linked_sessions || 0, icon: Video },
                { label: L.mappedLanes, value: links.total_lane_links || 0, icon: Layers },
                { label: L.competitions, value: links.competitions_linked || 0, icon: BarChart3 },
                { label: L.events, value: links.events_linked || 0, icon: Timer },
                { label: L.withOfficial, value: official.with_official_result || 0, icon: CheckCircle2 },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <item.icon size={12} style={{ color: T.textMute }} />
                    <span className="text-xs" style={{ color: T.textSoft }}>{item.label}</span>
                  </div>
                  <span className="text-sm font-bold" style={{ color: T.text }}>{item.value}</span>
                </div>
              ))}
            </div>
            {(totals.sessions || 0) > 0 && (links.linked_sessions || 0) === 0 && (
              <div className="mt-3 p-2 rounded-lg text-[10px] text-center" style={{ backgroundColor: T.warningBg, color: T.warning }}>
                {L.noLinked}
              </div>
            )}
          </div>
        </div>

        {/* ── Daily Trend ───────────────────────────────────────── */}
        {daily.length > 0 && (
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: T.border }}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={14} style={{ color: T.textSoft }} />
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
                {L.dailyTrend}
              </span>
            </div>
            <div className="flex items-end gap-1 h-24">
              {[...daily].reverse().map((d, i) => {
                const pct = (d.sessions / maxDaily) * 100;
                const compPct = (d.completed / maxDaily) * 100;
                return (
                  <div key={d.day || i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                    <div className="w-full relative" style={{ height: '80px' }}>
                      {/* Total bar */}
                      <div className="absolute bottom-0 w-full rounded-t"
                        style={{ height: `${pct}%`, backgroundColor: T.accentSoft }} />
                      {/* Completed bar */}
                      <div className="absolute bottom-0 w-full rounded-t"
                        style={{ height: `${compPct}%`, backgroundColor: T.accent }} />
                    </div>
                    <span className="text-[8px] font-semibold" style={{ color: T.textMute }}>
                      {d.day ? new Date(d.day + 'T00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : ''}
                    </span>
                    {/* Tooltip on hover */}
                    <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[9px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                      {L.sessionTooltip(d.sessions, d.completed)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-2 text-[10px]" style={{ color: T.textMute }}>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm" style={{ backgroundColor: T.accent }} /> {L.completedLegend}</div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm" style={{ backgroundColor: T.accentSoft }} /> {L.totalLegend}</div>
            </div>
          </div>
        )}

        {/* ── Recent Sessions ───────────────────────────────────── */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: T.border }}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: T.border }}>
            <div className="flex items-center gap-2">
              <Video size={14} style={{ color: T.textSoft }} />
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
                {L.recentSessions}
              </span>
            </div>
            <Link to="/swim-vision/sessions" className="text-[10px] font-bold flex items-center gap-1"
              style={{ color: T.accent }}>
              {L.viewAll} <ArrowRight size={10} />
            </Link>
          </div>
          {recent.length > 0 ? (
            <div>
              {recent.map((s, i) => <SessionRow key={s.session_id} s={s} index={i} onCancel={handleCancel} onReprocess={handleReprocess} />)}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Video size={24} style={{ color: T.textMute }} className="mx-auto mb-2" />
              <p className="text-xs font-semibold" style={{ color: T.textSoft }}>{L.noSessions}</p>
              <button onClick={() => navigate('/swim-vision')}
                className="mt-3 px-4 py-2 rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: T.accent }}>
                {L.startFirst}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SwimVisionDashboard;
